import { Address, ShippingMethod, ShippingMethodType, TTaxiShippingQuote } from "@core/types";
import { useCallback, useEffect, useMemo, useRef } from "react";
import debounce from "lodash.debounce";
import { useCalculateTaxiShippingMutation } from "../shippingAndPayment/useCalculateTaxiShippingMutation";
import { useOrderData } from "../orderDataStore";
import { useCartData } from "../cartDataStore";
import { useConfig } from "../config";
import { useActiveOrMainShippingZone } from "../shippingZones";
import { useGetShippingMethodsQuery } from "../shippingAndPayment";
import getEnabledShippingMethods from "../../utils/getEnabledShippingMethods";
import useGetCurrentAddress from "../../hooks/useGetCurrentAddress";
import { useCustomerAddressesStorageData } from "../customerAddressesStorage";
import {
  applyTaxiMasterShippingUpdate,
  dedupedCalculateTaxiShipping,
  getAddressRecalcKey,
  hasTaxiMasterShippingMethod,
  isFastCourierFeatureEnabled,
  isTaxiMasterAvailable as checkTaxiMasterAvailable,
} from "./taxiMasterShippingUtils";
import { UseMutateAsyncFunction } from "@tanstack/react-query";

type UseTaxiMasterShippingActionsOptions = {
  enabledShippingMethods?: ShippingMethod[];
  enableRecalc?: boolean;
};

type Res = {
  handleUpdateOrderData: ({
    isCourierServiceTurned,
    taxiShippingQuote,
    showInvalidCostToast,
  }: {
    isCourierServiceTurned: boolean;
    taxiShippingQuote?: TTaxiShippingQuote | null;
    showInvalidCostToast?: boolean;
  }) => boolean;
  calculateAndApplyTaxiCost: (
    address: Address | null,
    options?: { showInvalidCostToast?: boolean; isTurned?: boolean },
  ) => Promise<boolean>;
  calculateTaxiShippingMutateAsync: UseMutateAsyncFunction<
    TTaxiShippingQuote | null,
    Error,
    {
      customerAddress: Address;
    },
    unknown
  >;
  isCalculateTaxiShippingPending: boolean;
  isTaxiMasterFeatureEnabled: boolean;
  hasTaxiMasterMethod: boolean;
  isTaxiMasterAvailable: boolean;
  isTaxiMasterSelected: boolean;
  isTaxiMasterTurned: boolean;
  enabledShippingMethods: ShippingMethod[];
};

const RECALC_DEBOUNCE_MS = 500;

/**
 * * (Главная логика доставки такси) это глобальный "калькулятор и контроллер" бизнес-правил такси
 * * Это ядро, которое отвечает за все расчеты и проверки "под капотом".

* * Что делает: Проверяет, работает ли доставка такси в зоне клиента. Запрашивает и рассчитывает стоимость поездки до указанного адреса.
* * Автоматизация: Если клиент меняет адрес, хук сам замечает это и пересчитывает цену доставки, обновляя общие данные заказа.
 */
const useTaxiMasterShippingActions = (options?: UseTaxiMasterShippingActionsOptions): Res => {
  const config = useConfig();
  const { updateOrderData, deliveryDate, shippingMethod } = useOrderData();
  const { cartSubtotalValue, getProductsTotalWeight } = useCartData();
  const { activeShippingZone, isLoading: isActiveShippingZoneLoading } = useActiveOrMainShippingZone();
  const activeShippingZoneId = activeShippingZone.id;
  const recalcRequestIdRef = useRef(0);

  const customerAddress = useGetCurrentAddress();
  const { currentShippingAddressId } = useCustomerAddressesStorageData();

  const isTaxiMasterFeatureEnabled = isFastCourierFeatureEnabled(config);

  const shouldFetchShippingMethods =
    isTaxiMasterFeatureEnabled &&
    (!config.shipping.isShippingZonesEnabled || !!activeShippingZoneId) &&
    !isActiveShippingZoneLoading;

  const { data: fetchedShippingMethods } = useGetShippingMethodsQuery(
    String(activeShippingZoneId || ""),
    getProductsTotalWeight(),
    {
      enabled: shouldFetchShippingMethods,
    },
  );

  const enabledShippingMethods = options?.enabledShippingMethods?.length
    ? options.enabledShippingMethods
    : getEnabledShippingMethods(fetchedShippingMethods || []);

  const hasTaxiMasterMethod = hasTaxiMasterShippingMethod(enabledShippingMethods);
  const isTaxiMasterAvailableFlag = checkTaxiMasterAvailable({
    config,
    shippingMethods: enabledShippingMethods,
  });

  const isTaxiMasterSelected = shippingMethod?.methodType === ShippingMethodType.taxiMaster;

  const isTaxiMasterTurned = !!(isTaxiMasterAvailableFlag && isTaxiMasterSelected);

  const { mutateAsync: calculateTaxiShippingMutateAsync, isPending: isCalculateTaxiShippingPending } =
    useCalculateTaxiShippingMutation();

  const handleUpdateOrderData = useCallback(
    ({
      isCourierServiceTurned,
      taxiShippingQuote,
      showInvalidCostToast,
    }: {
      isCourierServiceTurned: boolean;
      taxiShippingQuote?: TTaxiShippingQuote | null;
      showInvalidCostToast?: boolean;
    }) => {
      return applyTaxiMasterShippingUpdate({
        isCourierServiceTurned,
        taxiShippingQuote,
        enabledShippingMethods,
        cartSubtotalValue,
        deliveryDate,
        updateOrderData,
        showInvalidCostToast,
      });
    },
    [enabledShippingMethods, cartSubtotalValue, deliveryDate, updateOrderData],
  );

  const calculateAndApplyTaxiCost = useCallback(
    async (address: Address | null, calculateOptions: { showInvalidCostToast?: boolean; isTurned?: boolean } = {}) => {
      const isTurned = calculateOptions.isTurned ?? true;

      if (!isTaxiMasterAvailableFlag || !isTurned || !address) {
        handleUpdateOrderData({ isCourierServiceTurned: false, showInvalidCostToast: false });
        return false;
      }

      const requestId = ++recalcRequestIdRef.current;

      try {
        const taxiShippingQuote = await dedupedCalculateTaxiShipping(address, calculateTaxiShippingMutateAsync);

        if (requestId !== recalcRequestIdRef.current) {
          return false;
        }

        handleUpdateOrderData({
          isCourierServiceTurned: true,
          taxiShippingQuote,
          showInvalidCostToast: calculateOptions.showInvalidCostToast ?? true,
        });
        return true;
      } catch {
        if (requestId !== recalcRequestIdRef.current) {
          return false;
        }

        handleUpdateOrderData({ isCourierServiceTurned: false, showInvalidCostToast: false });
        return false;
      }
    },
    [calculateTaxiShippingMutateAsync, handleUpdateOrderData, isTaxiMasterAvailableFlag],
  );

  // --- Recalc Logic ---
  const addressRecalcKey = useMemo(
    () => getAddressRecalcKey(currentShippingAddressId, customerAddress),
    [currentShippingAddressId, customerAddress],
  );

  const recalculateRef = useRef(calculateAndApplyTaxiCost);
  recalculateRef.current = calculateAndApplyTaxiCost;
  const handleUpdateOrderDataRef = useRef(handleUpdateOrderData);
  handleUpdateOrderDataRef.current = handleUpdateOrderData;
  const customerAddressRef = useRef(customerAddress);
  customerAddressRef.current = customerAddress;

  const debouncedRecalculate = useMemo(
    () =>
      debounce(() => {
        const address = customerAddressRef.current;
        if (!address) return;
        recalculateRef.current(address, { showInvalidCostToast: false });
      }, RECALC_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedRecalculate.cancel();
    };
  }, [debouncedRecalculate]);

  // * Сбрасываем TM из orderData, если feat flag выключен или метод недоступен в зоне
  useEffect(() => {
    if (!options?.enableRecalc) return;

    if (isTaxiMasterAvailableFlag || !isTaxiMasterSelected) {
      return;
    }

    handleUpdateOrderDataRef.current({ isCourierServiceTurned: false, showInvalidCostToast: false });
  }, [isTaxiMasterAvailableFlag, isTaxiMasterSelected, options?.enableRecalc]);

  // * Пересчёт только при реальной смене адреса (дедуп по addressRecalcKey)
  const lastRecalcKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!options?.enableRecalc) return;

    // * TM не активен — фиксируем текущий ключ как базу, чтобы повторное включение не слало лишний запрос
    if (!isTaxiMasterAvailableFlag || !isTaxiMasterTurned) {
      debouncedRecalculate.cancel();
      lastRecalcKeyRef.current = addressRecalcKey || null;
      return;
    }

    // * Адрес убрали — выключаем TM, без запроса
    if (!currentShippingAddressId || !addressRecalcKey) {
      debouncedRecalculate.cancel();
      lastRecalcKeyRef.current = null;
      handleUpdateOrderDataRef.current({ isCourierServiceTurned: false, showInvalidCostToast: false });
      return;
    }

    // * Адрес не изменился — ничего не делаем
    if (lastRecalcKeyRef.current === addressRecalcKey) {
      return;
    }

    lastRecalcKeyRef.current = addressRecalcKey;
    debouncedRecalculate();
  }, [
    addressRecalcKey,
    currentShippingAddressId,
    debouncedRecalculate,
    isTaxiMasterAvailableFlag,
    isTaxiMasterTurned,
    options?.enableRecalc,
  ]);

  return {
    handleUpdateOrderData,
    calculateAndApplyTaxiCost,
    calculateTaxiShippingMutateAsync,
    isCalculateTaxiShippingPending,
    isTaxiMasterFeatureEnabled,
    hasTaxiMasterMethod,
    isTaxiMasterAvailable: isTaxiMasterAvailableFlag,
    isTaxiMasterSelected,
    isTaxiMasterTurned,
    enabledShippingMethods,
  };
};

export default useTaxiMasterShippingActions;
